/* ---------------------------------------------------------------------------
(c) 2023-2024, HID Global Corporation, part of ASSA ABLOY.
All rights reserved.

This program is an un published copyrighted work which is proprietary
to HID Global Corporation. This computer program includes Confidential,
Proprietary Information and is a Trade Secret of HID Global Corporation.
Any use, disclosure, modification and/or reproduction is prohibited
unless authorized in writing by HID Global Corporation.

WARNING:  Unauthorized reproduction of this program as well as
unauthorized preparation of derivative works based upon the
program or distribution of copies by sale, rental, lease or
lending are violations of federal copyright laws and state trade
secret laws, punishable by civil and criminal penalties.
--------------------------------------------------------------------------- */
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import moment from 'moment';

import { AuthService } from '../auth.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { serverUrl } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService, private notificationService: NotificationService) {}

  private handleAuthError(err: HttpErrorResponse, req: HttpRequest<any>): Observable<any> {
    //handle your auth error or rethrow
    const errorCodes = [400, 404, 409, 500];
    // if the error status is 401 or 403 just clear the session and logout from the application
    if (err.status === 401) {
      this.authService.logout();
      return of(err.message); // or EMPTY may be appropriate here
    }
    // if the error codes are 400, 404, 409, 500  call the snackbar.
    else if (errorCodes.includes(err.status)) {
      switch (req.method) {
        case 'POST': {
          if (
            !(req.url.includes('/obtainDevice/') && err.error.errorCode === 0) &&
            !(err.status === 400 && err.error.error === 'invalid_grant') &&
            !req.url.includes('/pinUnlock/') &&
            !req.url.includes('/enroll/fido') &&
            !req.url.includes('/enroll/card')
          ) {
            this.notificationService.callSnackBar(err);
          }
          break;
        }
        case 'DELETE': {
          if (
            !(
              req.url.includes('/obtainDevice/') &&
              location.pathname.includes('/hid-approve-setup') &&
              err.status === 404
            )
          ) {
            this.notificationService.callSnackBar(err);
          }
          break;
        }
        default: {
          this.notificationService.callSnackBar(err);
        }
      }
    }
    return throwError(() => err);
  }

  private handleSuccess(req: HttpRequest<any>, success: any): any {
    const endPoints = ['scim', 'idp', 'ssp']; // valid endpoints
    endPoints.forEach((value) => {
      if (req?.url.indexOf('/' + value + '/') > -1) {
        if (success.status === 200) {
          this.authService.addToStorage({
            inactivityTimeValidDuration: moment()
              .add(this.authService.getDecodedInfo('inactivityTimeDuration') as string, 'milliseconds')
              .format()
          });
        }
      }
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If request is from scim APIs
    let authRequest = req.clone({ setHeaders: {} });
    const idpUrl = serverUrl.config.aasServer;
    const sspURL = serverUrl.config.sspServer;
    const accessToken = this.authService.getAccessToken();
    const idToken = this.authService.getIdToken();

    // If request is from authn register APIs
    if (req.url.indexOf('authn/register') > -1 || req.url.indexOf('authn/userinfo') > -1) {
      if (accessToken) {
        authRequest = req.clone({
          url: idpUrl + req.url,
          setHeaders: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + accessToken
          }
        });
      }
    } else if (req.url.indexOf('authn/enroll') > -1) {
      authRequest = req.clone({
        url: idpUrl + req.url,
        setHeaders: {
          'Content-Type': 'application/json'
        }
      });
    }
    // If request is from authn APIs
    else if (req.url.indexOf('authn') > -1) {
      authRequest = req.clone({
        url: idpUrl + req.url,
        setHeaders: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
    }
    // If request is from SSP APIs
    else if (req.url.indexOf('ssp') > -1) {
      if (idToken) {
        authRequest = req.clone({
          url: sspURL + req.url,
          setHeaders: {
            'Content-Type': 'application/scim+json',
            Authorization: 'Bearer ' + accessToken,
            id_token: idToken
          }
        });
      }
    }

    return next.handle(authRequest).pipe(
      tap((success) => this.handleSuccess(req, success)),
      catchError((x: any) => this.handleAuthError(x as HttpErrorResponse, req))
    );
  }
}
